(function terrainoProtectedDataModule(global) {
  'use strict';

  var PROXY_PREFIX = '/apps/terraino-hub/';
  var PROTECTED_PREFIX = 'v1/protected/';
  var SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  var ALLOWED_RICH_TEXT_TAGS = ['P', 'H2', 'H3', 'STRONG', 'EM', 'UL', 'OL', 'LI', 'BR', 'A'];
  var ALLOWED_RICH_TEXT_SET = new Set(ALLOWED_RICH_TEXT_TAGS);

  function text(element, value) {
    element.textContent = value == null ? '' : String(value);
    return element;
  }

  function generateNonce(cryptoObject) {
    var bytes = new Uint8Array(16);
    cryptoObject.getRandomValues(bytes);
    return Array.from(bytes, function toHex(value) { return value.toString(16).padStart(2, '0'); }).join('');
  }

  function buildProtectedUrl(endpoint, resourcePath, cryptoObject, origin) {
    var url = new URL(endpoint, origin);
    if (url.origin !== origin || !url.pathname.startsWith(PROXY_PREFIX)) {
      throw new Error('Protected endpoint must use the same-origin TERRAINO app proxy');
    }
    var derivedPath = url.pathname.slice(PROXY_PREFIX.length);
    if (!derivedPath.startsWith(PROTECTED_PREFIX) || derivedPath.includes('..') || derivedPath !== resourcePath) {
      throw new Error('Protected endpoint path binding did not match the requested resource');
    }
    url.searchParams.set('terraino_path', derivedPath);
    url.searchParams.set('terraino_nonce', generateNonce(cryptoObject));
    return url.toString();
  }

  function protectedError(message, status, code) {
    var error = new Error(message);
    error.name = 'TerrainoProtectedError';
    error.status = status;
    error.code = code;
    return error;
  }

  async function readJson(response) {
    try {
      return await response.json();
    } catch (_error) {
      throw protectedError('The protected endpoint did not return valid JSON', response.status, 'INVALID_RESPONSE');
    }
  }

  async function fetchProtected(options) {
    var url = buildProtectedUrl(
      options.endpoint,
      options.resourcePath,
      options.cryptoObject,
      options.origin,
    );
    var response = await options.fetchImpl(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: options.signal,
    });
    var payload = await readJson(response);
    if (!response.ok) {
      var code = payload && payload.error && typeof payload.error.code === 'string'
        ? payload.error.code
        : 'PROTECTED_REQUEST_FAILED';
      throw protectedError('Protected request failed', response.status, code);
    }
    if (!payload || payload.schemaVersion !== 1) {
      throw protectedError('Protected response did not match schema version 1', response.status, 'INVALID_RESPONSE');
    }
    return payload;
  }

  function normalizedAllowedUrl(value, origin, protocols) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      var parsed = new URL(value, origin);
      return protocols.includes(parsed.protocol) ? parsed.href : null;
    } catch (_error) {
      return null;
    }
  }

  function safeExternalUrl(value, origin) {
    return normalizedAllowedUrl(value, origin, ['http:', 'https:', 'mailto:']);
  }

  function safeMaterialSource(value, origin) {
    return normalizedAllowedUrl(value, origin, ['http:', 'https:']);
  }

  function copyAllowedRichText(documentObject, html, origin) {
    var fragment = documentObject.createDocumentFragment();
    if (typeof html !== 'string' || !html) return fragment;
    var parser = new global.DOMParser();
    var parsed = parser.parseFromString(html, 'text/html');

    function copyNode(sourceNode, target) {
      if (sourceNode.nodeType === 3) {
        target.appendChild(documentObject.createTextNode(sourceNode.textContent || ''));
        return;
      }
      if (sourceNode.nodeType !== 1) return;
      var tag = sourceNode.tagName.toUpperCase();
      if (!ALLOWED_RICH_TEXT_SET.has(tag)) {
        Array.from(sourceNode.childNodes).forEach(function copyUnwrapped(child) { copyNode(child, target); });
        return;
      }
      var destination = documentObject.createElement(tag.toLowerCase());
      if (tag === 'A') {
        var href = safeExternalUrl(sourceNode.getAttribute('href'), origin);
        if (href) destination.setAttribute('href', href);
      }
      Array.from(sourceNode.childNodes).forEach(function copyChild(child) { copyNode(child, destination); });
      target.appendChild(destination);
    }

    Array.from(parsed.body.childNodes).forEach(function copyRoot(child) { copyNode(child, fragment); });
    return fragment;
  }

  function heading(documentObject, level, label) {
    return text(documentObject.createElement('h' + level), label);
  }

  async function shareBuildUrl(options) {
    var navigatorObject = options.navigatorObject || {};
    var payload = { title: options.title || 'TERRAINO Build', url: options.url };
    if (typeof navigatorObject.share === 'function') {
      try {
        await navigatorObject.share(payload);
        return 'shared';
      } catch (error) {
        if (error && error.name === 'AbortError') return 'cancelled';
      }
    }
    if (navigatorObject.clipboard && typeof navigatorObject.clipboard.writeText === 'function') {
      await navigatorObject.clipboard.writeText(options.url);
      return 'copied';
    }
    return 'unavailable';
  }

  function nextGalleryIndex(index, direction, length) {
    if (!Number.isInteger(length) || length < 1) return 0;
    return (Number(index) + Number(direction) + length) % length;
  }

  function initializeBuildDetailInteractions() {
    var documentObject = global.document;
    if (!documentObject) return;

    documentObject.querySelectorAll('[data-terraino-build-share]').forEach(function initializeShare(button) {
      var status = button.parentElement.querySelector('[data-terraino-build-share-status]');
      button.addEventListener('click', async function shareBuild() {
        button.disabled = true;
        try {
          var result = await shareBuildUrl({
            navigatorObject: global.navigator,
            title: button.dataset.terrainoBuildShareTitle,
            url: button.dataset.terrainoBuildShareUrl,
          });
          if (result === 'copied') text(status, 'Link copied to clipboard.');
          else if (result === 'shared') text(status, 'Share options opened.');
          else if (result === 'unavailable') {
            global.prompt('Copy this Build link:', button.dataset.terrainoBuildShareUrl);
            text(status, 'Copy the link from the dialog.');
          }
        } catch (_error) {
          global.prompt('Copy this Build link:', button.dataset.terrainoBuildShareUrl);
          text(status, 'Copy the link from the dialog.');
        } finally {
          button.disabled = false;
        }
      });
    });

    documentObject.querySelectorAll('[data-terraino-hub-surface="build-detail"]').forEach(function initializeGallery(build) {
      var dialog = build.querySelector('[data-terraino-build-gallery-dialog]');
      var gallery = build.querySelector('[data-terraino-build-gallery]');
      var heroTrigger = build.querySelector('[data-terraino-build-hero-trigger]');
      if (!dialog || (!gallery && !heroTrigger)) return;
      var image = dialog.querySelector('[data-terraino-build-gallery-image]');
      var caption = dialog.querySelector('[data-terraino-build-gallery-caption]');
      var thumbnails = gallery
        ? Array.from(gallery.querySelectorAll('[data-terraino-build-gallery-thumbnail]'))
        : [];
      var galleryItems = heroTrigger ? [heroTrigger].concat(thumbnails) : thumbnails;
      var activeIndex = 0;
      var start = null;
      var returnFocusTarget = null;

      function showImage(index) {
        if (!galleryItems.length) return;
        activeIndex = nextGalleryIndex(index, 0, galleryItems.length);
        var galleryItem = galleryItems[activeIndex];
        image.src = galleryItem.dataset.terrainoBuildGallerySrc;
        image.alt = galleryItem.dataset.terrainoBuildGalleryAlt || '';
        text(caption, (activeIndex + 1) + ' of ' + galleryItems.length);
      }

      function openGallery(index) {
        if (!galleryItems.length) return;
        returnFocusTarget = galleryItems[nextGalleryIndex(index, 0, galleryItems.length)];
        showImage(index);
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }

      function closeGallery() {
        if (typeof dialog.close === 'function') dialog.close();
        else dialog.removeAttribute('open');
        if (returnFocusTarget && typeof returnFocusTarget.focus === 'function') {
          returnFocusTarget.focus({ preventScroll: true });
        }
      }

      if (heroTrigger) heroTrigger.addEventListener('click', function openHero() { openGallery(0); });
      thumbnails.forEach(function bindThumbnail(thumbnail, index) {
        thumbnail.addEventListener('click', function openThumbnail() { openGallery(heroTrigger ? index + 1 : index); });
      });
      dialog.querySelector('[data-terraino-build-gallery-close]').addEventListener('click', function closeGalleryButton() {
        closeGallery();
      });
      dialog.querySelector('[data-terraino-build-gallery-previous]').addEventListener('click', function previousImage() {
        showImage(nextGalleryIndex(activeIndex, -1, galleryItems.length));
      });
      dialog.querySelector('[data-terraino-build-gallery-next]').addEventListener('click', function nextImage() {
        showImage(nextGalleryIndex(activeIndex, 1, galleryItems.length));
      });
      dialog.addEventListener('keydown', function galleryKeys(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeGallery();
          return;
        }
        if (event.key === 'ArrowLeft') showImage(nextGalleryIndex(activeIndex, -1, galleryItems.length));
        if (event.key === 'ArrowRight') showImage(nextGalleryIndex(activeIndex, 1, galleryItems.length));
      });
      dialog.addEventListener('cancel', function galleryCancel(event) {
        event.preventDefault();
        closeGallery();
      });
      dialog.addEventListener('pointerdown', function beginSwipe(event) {
        start = { x: event.clientX, y: event.clientY };
      });
      dialog.addEventListener('pointerup', function endSwipe(event) {
        if (!start) return;
        var deltaX = event.clientX - start.x;
        var deltaY = event.clientY - start.y;
        start = null;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        showImage(nextGalleryIndex(activeIndex, deltaX < 0 ? 1 : -1, galleryItems.length));
      });
    });
  }

  function renderPieces(documentObject, payload, origin) {
    var section = documentObject.createElement('section');
    section.className = 'terraino-hub__breakdown-section';
    section.appendChild(heading(documentObject, 3, 'Pieces'));
    var grid = documentObject.createElement('div');
    grid.className = 'terraino-hub__piece-grid';
    payload.pieces.forEach(function renderPiece(piece) {
      var hasPiecePage = SLUG_PATTERN.test(String(piece.slug || ''));
      var card = documentObject.createElement(hasPiecePage ? 'a' : 'article');
      card.className = 'terraino-hub__piece-card terraino-hub__piece-card--build';
      if (hasPiecePage) {
        card.href = '/pages/piece-catalog?piece=' + encodeURIComponent(piece.slug);
        card.setAttribute('aria-label', 'Open ' + (piece.name || 'this piece') + ' in the Piece Catalog');
      }
      if (piece.heroImage && piece.heroImage.url) {
        var imageUrl = safeMaterialSource(piece.heroImage.url, origin);
        if (imageUrl) {
          var image = documentObject.createElement('img');
          image.src = imageUrl;
          image.alt = piece.heroImage.altText || '';
          image.loading = 'lazy';
          card.appendChild(image);
        }
      }
      var title = text(documentObject.createElement('span'), piece.name || 'Unnamed piece');
      title.className = 'terraino-hub__piece-card-title';
      card.appendChild(title);
      grid.appendChild(card);
    });
    if (!payload.pieces.length) grid.appendChild(text(documentObject.createElement('p'), 'No pieces are listed for this Build.'));
    section.appendChild(grid);
    return section;
  }

  function renderMaterials(documentObject, payload, origin) {
    var section = documentObject.createElement('section');
    section.className = 'terraino-hub__breakdown-section';
    section.appendChild(heading(documentObject, 3, 'Materials'));
    var list = documentObject.createElement('div');
    list.className = 'terraino-hub__material-list';
    payload.materials.forEach(function renderMaterial(material) {
      var card = documentObject.createElement('article');
      card.className = 'terraino-hub__material-card';
      card.appendChild(heading(documentObject, 4, material.name || 'Unnamed material'));
      if (material.count || material.countType) {
        var amount = [material.count, material.countType].filter(Boolean).join(' ');
        if (material.hasAdditional) amount += ' plus additional as needed';
        card.appendChild(text(documentObject.createElement('p'), amount));
      } else if (material.hasAdditional) {
        card.appendChild(text(documentObject.createElement('p'), 'Additional amount as needed'));
      }
      if (material.notes) card.appendChild(text(documentObject.createElement('p'), material.notes));
      if (Array.isArray(material.sources) && material.sources.length) {
        var sourceList = documentObject.createElement('ul');
        material.sources.forEach(function renderSource(source) {
          var href = safeMaterialSource(source.url, origin);
          if (!href) return;
          var item = documentObject.createElement('li');
          var link = documentObject.createElement('a');
          link.href = href;
          link.rel = 'noopener';
          text(link, source.friendlyName || 'Material source');
          item.appendChild(link);
          sourceList.appendChild(item);
        });
        if (sourceList.childNodes.length) card.appendChild(sourceList);
      }
      list.appendChild(card);
    });
    if (!payload.materials.length) list.appendChild(text(documentObject.createElement('p'), 'No additional materials are listed.'));
    section.appendChild(list);
    if (payload.materialsNotesHtml) {
      var notes = documentObject.createElement('div');
      notes.className = 'terraino-hub__material-notes rte';
      notes.appendChild(copyAllowedRichText(documentObject, payload.materialsNotesHtml, origin));
      section.appendChild(notes);
    }
    return section;
  }

  function validBuildPayload(payload) {
    return payload
      && payload.schemaVersion === 1
      && Array.isArray(payload.pieces)
      && Array.isArray(payload.materials)
      && typeof payload.materialsNotesHtml === 'string';
  }

  function renderBuildBreakdown(documentObject, root, payload, origin) {
    if (!validBuildPayload(payload)) throw protectedError('Protected Build response was incomplete', 200, 'INVALID_RESPONSE');
    var wrapper = documentObject.createElement('div');
    wrapper.className = 'terraino-hub__breakdown';
    wrapper.appendChild(renderPieces(documentObject, payload, origin));
    wrapper.appendChild(renderMaterials(documentObject, payload, origin));
    root.replaceChildren(wrapper);
  }

  function renderLoading(documentObject, root) {
    var state = documentObject.createElement('div');
    state.className = 'terraino-hub__access-state';
    state.setAttribute('role', 'status');
    text(state, 'Opening the protected Build reference…');
    root.replaceChildren(state);
  }

  function renderError(documentObject, root, error, retry) {
    var state = documentObject.createElement('div');
    state.className = 'terraino-hub__access-state';
    var message = 'The protected reference could not be loaded. The public Build page is still available.';
    if (error.code === 'SIGNED_OUT') message = 'Your customer session has ended. Sign in again to open this reference.';
    if (error.code === 'ESSENTIAL_CORE_REQUIRED') message = 'Essential Core access is required to open this Build reference.';
    text(state.appendChild(documentObject.createElement('p')), message);
    if (error.code === 'SIGNED_OUT') {
      var signIn = documentObject.createElement('a');
      signIn.className = 'button button--primary';
      signIn.href = '/account/login?return_url=' + encodeURIComponent(global.location.pathname + global.location.search);
      text(signIn, 'Sign in');
      state.appendChild(signIn);
    } else if (error.code !== 'ESSENTIAL_CORE_REQUIRED') {
      var button = documentObject.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary';
      button.setAttribute('data-terraino-protected-retry', '');
      text(button, 'Retry');
      button.addEventListener('click', retry);
      state.appendChild(button);
    }
    root.replaceChildren(state);
  }

  function initializeBuild(root) {
    var endpoint = root.dataset.terrainoEndpoint;
    var resourcePath = root.dataset.terrainoResourcePath;
    if (!endpoint || !resourcePath) return;
    var controller = null;

    async function load() {
      if (controller) controller.abort();
      controller = new AbortController();
      renderLoading(global.document, root);
      try {
        var payload = await fetchProtected({
          endpoint: endpoint,
          resourcePath: resourcePath,
          fetchImpl: global.fetch.bind(global),
          cryptoObject: global.crypto,
          origin: global.location.origin,
          signal: controller.signal,
        });
        renderBuildBreakdown(global.document, root, payload, global.location.origin);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        renderError(global.document, root, error || {}, load);
      }
    }

    load();
  }

  function appendPieceImage(documentObject, container, image, origin) {
    if (!image || !image.url) return;
    var imageUrl = safeMaterialSource(image.url, origin);
    if (!imageUrl) return;
    var element = documentObject.createElement('img');
    element.src = imageUrl;
    element.alt = image.altText || '';
    element.loading = 'lazy';
    container.appendChild(element);
  }

  function renderPieceList(documentObject, root, payload, options) {
    if (!payload || payload.schemaVersion !== 1 || !Array.isArray(payload.pieces)) {
      throw protectedError('Protected Piece list response was incomplete', 200, 'INVALID_RESPONSE');
    }
    var wrapper = documentObject.createElement('div');
    wrapper.className = 'terraino-hub__catalog-view';
    var summary = documentObject.createElement('p');
    summary.className = 'terraino-hub__results-status';
    summary.setAttribute('role', 'status');
    text(summary, payload.totalItems + (payload.totalItems === 1 ? ' piece' : ' pieces'));
    wrapper.appendChild(summary);

    var grid = documentObject.createElement('div');
    grid.className = 'terraino-hub__piece-grid terraino-hub__piece-grid--catalog';
    payload.pieces.forEach(function renderCard(piece) {
      var card = documentObject.createElement('article');
      card.className = 'terraino-hub__piece-card';
      appendPieceImage(documentObject, card, piece.heroImage, options.origin);
      card.appendChild(heading(documentObject, 3, piece.name || 'Unnamed piece'));
      var meta = [piece.pieceTypeName, piece.pieceSubtypeName, piece.difficulty].filter(Boolean).join(' · ');
      if (meta) card.appendChild(text(documentObject.createElement('p'), meta));
      if (piece.builderName) card.appendChild(text(documentObject.createElement('p'), 'Designed by ' + piece.builderName));
      if (piece.description) {
        var description = documentObject.createElement('div');
        description.className = 'rte';
        description.appendChild(copyAllowedRichText(documentObject, piece.description, options.origin));
        card.appendChild(description);
      }
      if (SLUG_PATTERN.test(String(piece.slug || ''))) {
        var open = documentObject.createElement('button');
        open.type = 'button';
        open.className = 'button button--secondary';
        open.setAttribute('data-terraino-piece-open', piece.slug);
        text(open, 'Open piece');
        open.addEventListener('click', function openPiece() { options.onOpen(piece.slug); });
        card.appendChild(open);
      }
      grid.appendChild(card);
    });
    if (!payload.pieces.length) grid.appendChild(text(documentObject.createElement('p'), 'No matching pieces were found.'));
    wrapper.appendChild(grid);

    if (payload.totalPages > 1) {
      var pagination = documentObject.createElement('nav');
      pagination.className = 'terraino-hub__pagination';
      pagination.setAttribute('aria-label', 'Piece pages');
      if (payload.page > 1) {
        var previous = documentObject.createElement('button');
        previous.type = 'button';
        text(previous, 'Previous');
        previous.addEventListener('click', function previousPage() { options.onPage(payload.page - 1); });
        pagination.appendChild(previous);
      }
      pagination.appendChild(text(documentObject.createElement('span'), 'Page ' + payload.page + ' of ' + payload.totalPages));
      if (payload.page < payload.totalPages) {
        var next = documentObject.createElement('button');
        next.type = 'button';
        text(next, 'Next');
        next.addEventListener('click', function nextPage() { options.onPage(payload.page + 1); });
        pagination.appendChild(next);
      }
      wrapper.appendChild(pagination);
    }
    root.replaceChildren(wrapper);
  }

  function renderPieceProducts(documentObject, piece, origin) {
    var section = documentObject.createElement('section');
    section.className = 'terraino-hub__piece-products';
    section.appendChild(heading(documentObject, 3, 'Available in'));
    var grid = documentObject.createElement('div');
    grid.className = 'terraino-hub__unlock-grid';
    (piece.products || []).forEach(function renderProduct(product) {
      var card = documentObject.createElement('article');
      card.className = 'terraino-hub__unlock-card';
      appendPieceImage(documentObject, card, product.imageUrl ? { url: product.imageUrl, altText: '' } : null, origin);
      card.appendChild(heading(documentObject, 4, product.title || 'TERRAINO product'));
      if (product.priceLabel) card.appendChild(text(documentObject.createElement('p'), product.priceLabel));
      var href = safeExternalUrl(product.href, origin);
      if (product.available && href) {
        var link = documentObject.createElement('a');
        link.className = 'button button--secondary';
        link.href = href;
        text(link, 'View product');
        card.appendChild(link);
      }
      grid.appendChild(card);
    });
    if (!grid.childNodes.length) grid.appendChild(text(documentObject.createElement('p'), 'No shop products are linked to this piece.'));
    section.appendChild(grid);
    return section;
  }

  function galleryImagesFor(piece) {
    return Array.isArray(piece && piece.images)
      ? piece.images.filter(function withoutHero(image) { return image && image.isHero !== true; })
      : [];
  }

  function renderPieceDetail(documentObject, root, payload, options) {
    var piece = payload && payload.piece;
    if (!payload || payload.schemaVersion !== 1 || !piece || !SLUG_PATTERN.test(String(piece.slug || ''))) {
      throw protectedError('Protected Piece detail response was incomplete', 200, 'INVALID_RESPONSE');
    }
    var wrapper = documentObject.createElement('article');
    wrapper.className = 'terraino-hub__piece-detail';
    var back = documentObject.createElement('button');
    back.type = 'button';
    back.className = 'terraino-hub__text-link terraino-hub__piece-back';
    text(back, '← Back to Piece Catalog');
    back.addEventListener('click', options.onBack);
    wrapper.appendChild(back);

    var headingBlock = documentObject.createElement('header');
    headingBlock.className = 'terraino-hub__piece-detail-heading';
    var media = documentObject.createElement('div');
    media.className = 'terraino-hub__piece-detail-media';
    appendPieceImage(documentObject, media, piece.heroImage, options.origin);
    headingBlock.appendChild(media);
    var copy = documentObject.createElement('div');
    copy.appendChild(heading(documentObject, 2, piece.name));
    var meta = [piece.pieceTypeName, piece.pieceSubtypeName, piece.difficulty].filter(Boolean).join(' · ');
    if (meta) copy.appendChild(text(documentObject.createElement('p'), meta));
    if (piece.builderName) copy.appendChild(text(documentObject.createElement('p'), 'Designed by ' + piece.builderName));
    if (piece.description) copy.appendChild(copyAllowedRichText(documentObject, piece.description, options.origin));
    headingBlock.appendChild(copy);
    wrapper.appendChild(headingBlock);

    var galleryImages = galleryImagesFor(piece);
    if (galleryImages.length) {
      var gallery = documentObject.createElement('div');
      gallery.className = 'terraino-hub__gallery';
      galleryImages.forEach(function appendGalleryImage(image) { appendPieceImage(documentObject, gallery, image, options.origin); });
      wrapper.appendChild(gallery);
    }
    if (Array.isArray(piece.kitProxies) && piece.kitProxies.length) {
      var family = documentObject.createElement('section');
      family.appendChild(heading(documentObject, 3, 'TERRAINO piece families'));
      var list = documentObject.createElement('ul');
      piece.kitProxies.forEach(function appendFamily(item) { list.appendChild(text(documentObject.createElement('li'), item.name)); });
      family.appendChild(list);
      wrapper.appendChild(family);
    }
    wrapper.appendChild(renderPieceProducts(documentObject, piece, options.origin));
    root.replaceChildren(wrapper);
  }

  function initializePieceCatalog(root) {
    var endpoint = root.dataset.terrainoEndpoint;
    var resourcePath = root.dataset.terrainoResourcePath;
    if (!endpoint || !resourcePath) return;
    var scope = root.closest('[data-terraino-hub]') || global.document;
    var form = scope.querySelector('[data-terraino-piece-search-form]');
    var queryInput = scope.querySelector('[data-terraino-piece-search]');
    var controller = null;
    var currentQuery = '';
    var currentPage = 1;

    function abortCurrent() {
      if (controller) controller.abort();
      controller = new AbortController();
      return controller.signal;
    }

    async function loadList(query, page) {
      currentQuery = String(query || '').trim().slice(0, 120);
      currentPage = Number.isInteger(page) && page > 0 ? page : 1;
      renderLoading(global.document, root);
      var listUrl = new URL(endpoint, global.location.origin);
      if (currentQuery) listUrl.searchParams.set('query', currentQuery);
      if (currentPage > 1) listUrl.searchParams.set('page', String(currentPage));
      try {
        var payload = await fetchProtected({
          endpoint: listUrl.toString(),
          resourcePath: resourcePath,
          fetchImpl: global.fetch.bind(global),
          cryptoObject: global.crypto,
          origin: global.location.origin,
          signal: abortCurrent(),
        });
        renderPieceList(global.document, root, payload, {
          origin: global.location.origin,
          onOpen: function onOpen(slug) { openDetail(slug, true); },
          onPage: function onPage(nextPage) { loadList(currentQuery, nextPage); },
        });
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        renderError(global.document, root, error || {}, function retryList() { loadList(currentQuery, currentPage); });
      }
    }

    async function openDetail(slug, pushHistory) {
      if (!SLUG_PATTERN.test(String(slug || ''))) {
        loadList(currentQuery, 1);
        return;
      }
      if (pushHistory) {
        var pageUrl = new URL(global.location.href);
        pageUrl.searchParams.set('piece', slug);
        global.history.pushState({}, '', pageUrl.pathname + pageUrl.search);
      }
      renderLoading(global.document, root);
      try {
        var payload = await fetchProtected({
          endpoint: endpoint + '/' + encodeURIComponent(slug),
          resourcePath: resourcePath + '/' + slug,
          fetchImpl: global.fetch.bind(global),
          cryptoObject: global.crypto,
          origin: global.location.origin,
          signal: abortCurrent(),
        });
        renderPieceDetail(global.document, root, payload, {
          origin: global.location.origin,
          onBack: function onBack() {
            var pageUrl = new URL(global.location.href);
            pageUrl.searchParams.delete('piece');
            global.history.pushState({}, '', pageUrl.pathname + pageUrl.search);
            loadList(currentQuery, 1);
          },
        });
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        renderError(global.document, root, error || {}, function retryDetail() { openDetail(slug, false); });
      }
    }

    function routeFromLocation() {
      var slug = new URL(global.location.href).searchParams.get('piece');
      if (slug && SLUG_PATTERN.test(slug)) openDetail(slug, false);
      else loadList(queryInput ? queryInput.value : '', 1);
    }

    if (form) {
      form.addEventListener('submit', function submitSearch(event) {
        event.preventDefault();
        var pageUrl = new URL(global.location.href);
        pageUrl.searchParams.delete('piece');
        global.history.pushState({}, '', pageUrl.pathname + pageUrl.search);
        loadList(queryInput ? queryInput.value : '', 1);
      });
    }
    global.addEventListener('popstate', routeFromLocation);
    routeFromLocation();
  }

  var api = {
    ALLOWED_RICH_TEXT_TAGS: ALLOWED_RICH_TEXT_TAGS,
    generateNonce: generateNonce,
    buildProtectedUrl: buildProtectedUrl,
    fetchProtected: fetchProtected,
    safeExternalUrl: safeExternalUrl,
    safeMaterialSource: safeMaterialSource,
    copyAllowedRichText: copyAllowedRichText,
    shareBuildUrl: shareBuildUrl,
    nextGalleryIndex: nextGalleryIndex,
    renderBuildBreakdown: renderBuildBreakdown,
    renderPieces: renderPieces,
    renderPieceList: renderPieceList,
    renderPieceDetail: renderPieceDetail,
    galleryImagesFor: galleryImagesFor,
    initializeBuild: initializeBuild,
    initializePieceCatalog: initializePieceCatalog,
  };
  global.TerrainoProtectedData = api;

  if (global.document) {
    initializeBuildDetailInteractions();
    global.document.querySelectorAll('[data-terraino-protected-build]').forEach(initializeBuild);
    global.document.querySelectorAll('[data-terraino-piece-catalog]').forEach(initializePieceCatalog);
  }
})(globalThis);
